import axios from 'axios'

/**
 * Which credential a 401 refers to.
 *
 * The session credential is the caller's own Firebase token, which renewing can fix. The adapter
 * credential is the EHR's session, which it cannot: that one is restored by connecting the
 * integration again, so spending a renewal on it only costs a token exchange and misleads the user
 * about what went wrong (PROD-FASTAPI-DE).
 */
export type CredentialScope = 'session' | 'adapter'

// The code the platform API puts on a 401 when an EHR refused the integration's session. Like
// ACCOUNT_DISABLED_DETAIL in `api.ts`, the two sides are coupled by this exact string.
const ADAPTER_UNAUTHORIZED_CODE = 'adapter_session_expired'

const responseCode = (error: unknown): string | undefined => {
    if (!axios.isAxiosError(error)) {
        return undefined
    }
    const data: unknown = error.response?.data
    if (typeof data !== 'object' || data === null || !('code' in data)) {
        return undefined
    }
    return typeof data.code === 'string' ? data.code : undefined
}

/**
 * Names the credential the server refused.
 *
 * Only the backend knows, and it says so: every adapter 401 is raised as `AdapterSessionExpired`
 * and carries the adapter code. Anything else — `session_expired`, or a 401 without a code — is
 * the caller's own session, which is what every 401 was taken to mean before the code existed.
 */
export function credentialScope(error: unknown): CredentialScope {
    return responseCode(error) === ADAPTER_UNAUTHORIZED_CODE ? 'adapter' : 'session'
}
